import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlayCircleIcon, PauseCircleIcon, ForwardIcon, BackwardIcon, MusicalNoteIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { downloadAudioFile, downloadAllAudioAsZip } from '../../../services/audio/downloadHelper';
import { notify } from '../../../utils/notify.jsx';

/**
 * Compact playlist audio player.
 * Props:
 * - items: Array<{ id: string, title?: string, url: string, durationSec?: number }>
 * - label?: string
 * - onOpenGenerateAudio?: () => void
 * - onOpenOrder?: () => void
 */
export default function AudioPlaylistPlayer({ items = [], label = 'Full Narration', onOpenGenerateAudio, onOpenOrder }) {
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const audioRef = useRef(null);
  const autoplayNextRef = useRef(false);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Cleanup: Stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch {}
        audioRef.current = null;
      }
    };
  }, []);

  const hasItems = (items && items.length > 0);

  // Decide contextual labels for action buttons
  const isFullNarration = (label || '').toLowerCase().includes('full narration');
  const orderBtnText = isFullNarration ? 'Reorder' : 'Order';
  const orderBtnTitle = isFullNarration ? 'Reorder narration items' : 'Order narration on this spread';
  const generateBtnText = isFullNarration ? 'Generate Audio' : 'Generate';
  const generateBtnTitle = isFullNarration ? 'Generate full narration audio' : 'Generate audio';

  const current = useMemo(() => {
    if (!hasItems) return null;
    const i = Math.min(Math.max(0, index), items.length - 1);
    return items[i];
  }, [items, index, hasItems]);

  const currentSpreadId = useMemo(() => {
    const rawId = current?.id || '';
    if (!rawId) return null;
    if (rawId.includes(':')) return rawId.split(':')[0];
    return current?.spreadId || null;
  }, [current?.id, current?.spreadId]);

  const fmt = (sec) => {
    if (!sec && sec !== 0) return '—:—';
    const s = Math.max(0, Math.round(sec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  const loadAudio = (url) => {
    if (!url) return null;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    const a = new Audio(url);
    a.addEventListener('ended', () => {
      const total = itemsRef.current?.length || 0;
      setIndex((i) => {
        const next = i + 1;
        if (next < total) {
          autoplayNextRef.current = true;
          return next;
        }
        // At end: stop and stay on last track
        autoplayNextRef.current = false;
        return i;
      });
      setIsPlaying(false);
      setProgress(0);
      setElapsedSec(0);
    });
    a.addEventListener('error', () => {
      console.error('[AudioPlaylistPlayer] Audio playback error');
      setIsPlaying(false);
      setProgress(0);
      setElapsedSec(0);
    });
    a.addEventListener('timeupdate', () => {
      const d = a.duration || 0;
      if (d > 0) {
        setProgress(Math.min(1, Math.max(0, a.currentTime / d)));
        setElapsedSec(Math.round(a.currentTime));
      }
    });
    a.addEventListener('loadedmetadata', () => {
      setDurationSec(Math.round(a.duration || 0));
    });
    audioRef.current = a;
    return a;
  };

  // When current track changes, load new audio (do not autoplay by default)
  useEffect(() => {
    if (current?.url) {
      loadAudio(current.url);
      setProgress(0);
      setElapsedSec(0);
      // Navigate the canvas to the spread of the current track
      try {
        const rawId = current?.id || '';
        const spreadId = (rawId.includes(':') ? rawId.split(':')[0] : (current?.spreadId || null));
        if (spreadId && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('sbw:go-to-spread', { detail: { spreadId } }));
        }
      } catch {}
      if (autoplayNextRef.current) {
        autoplayNextRef.current = false;
        (async () => {
          try {
            await audioRef.current?.play();
            setIsPlaying(true);
          } catch {
            setIsPlaying(false);
          }
        })();
      } else {
        setIsPlaying(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.url]);

  const handlePlayPause = async () => {
    if (!current?.url) return;
    try {
      let a = audioRef.current;
      if (!a) a = loadAudio(current.url);
      if (!a) return;
      if (isPlaying) {
        a.pause();
        setIsPlaying(false);
      } else {
        await a.play();
        setIsPlaying(true);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  const handlePrev = () => {
    if (!hasItems) return;
    setIndex((i) => Math.max(0, i - 1));
  };

  const handleNext = () => {
    if (!hasItems) return;
    setIndex((i) => Math.min(items.length - 1, i + 1));
  };

  const handleSeek = (clientX, trackEl) => {
    const a = audioRef.current;
    if (!a || !trackEl) return;
    const rect = trackEl.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const d = a.duration || 0;
    if (d > 0) {
      a.currentTime = d * ratio;
      setProgress(ratio);
      setElapsedSec(Math.round(a.currentTime));
    }
  };

  const handleDownloadSingle = async (item) => {
    if (!item?.url) return;
    try {
      setIsDownloading(true);
      const title = item.title || 'audio';
      const filename = `${title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.mp3`;
      await downloadAudioFile(item.url, filename);
      notify.success('Audio downloaded', { toastId: 'audio-download-single' });
    } catch (error) {
      console.error('Download failed:', error);
      notify.error('Failed to download audio', { toastId: 'audio-download-single-error' });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!hasItems) return;
    try {
      setIsDownloading(true);
      notify.loading('Preparing download...', { toastId: 'audio-download-all' });
      await downloadAllAudioAsZip(items, label);
      notify.dismiss('audio-download-all');
      notify.success('All audio files downloaded', { toastId: 'audio-download-all-success' });
    } catch (error) {
      console.error('Download all failed:', error);
      notify.dismiss('audio-download-all');
      notify.error('Failed to download audio files', { toastId: 'audio-download-all-error' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white/90 backdrop-blur border border-gray-200 shadow-md"
      role="group"
      aria-label="Full narration playlist player"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="flex items-center gap-1 text-gray-600">
        <MusicalNoteIcon className="w-4 h-4" />
        <span className="text-xs font-medium">{label}</span>
      </div>

      

      {/* Prev */}
      <button
        type="button"
        className="text-gray-700 disabled:opacity-40"
        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
        disabled={!hasItems || index === 0}
        title="Previous"
      >
        <BackwardIcon className="w-5 h-5" />
      </button>

      {/* Play/Pause */}
      <button
        type="button"
        className="text-gray-800 disabled:opacity-50"
        onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
        disabled={!current?.url}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <PauseCircleIcon className="w-6 h-6" /> : <PlayCircleIcon className="w-6 h-6" />}
      </button>

      {/* Next */}
      <button
        type="button"
        className="text-gray-700 disabled:opacity-40"
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        disabled={!hasItems || index >= items.length - 1}
        title="Next"
      >
        <ForwardIcon className="w-5 h-5" />
      </button>

      {/* Progress */}
      <div
        className="relative h-2 w-28 bg-gray-200 rounded-full cursor-pointer"
        onMouseDown={(e) => { e.stopPropagation(); handleSeek(e.clientX, e.currentTarget); }}
        onClick={(e) => { e.stopPropagation(); handleSeek(e.clientX, e.currentTarget); }}
        title="Seek"
      >
        <div
          className="absolute top-0 left-0 h-2 bg-indigo-500 rounded-full"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
        <div
          className="absolute top-1/2 w-3 h-3 bg-indigo-600 rounded-full shadow"
          style={{ left: `${Math.round(progress * 100)}%`, transform: 'translate(-50%, -50%)' }}
        />
      </div>

      {/* Time + Index */}
      <div className="flex items-center gap-2 text-[10px] tabular-nums text-gray-700">
        <span>{fmt(elapsedSec)} / {fmt(durationSec)}</span>
        <span className="text-gray-400">•</span>
        <span>{hasItems ? `${index + 1}/${items.length}` : '0/0'}</span>
      </div>

      {/* Track selector */}
      <select
        className="ml-2 text-xs border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
        disabled={!hasItems}
        value={hasItems ? current.id : ''}
        onChange={(e) => {
          const i = items.findIndex((it) => it.id === e.target.value);
          setIndex(i >= 0 ? i : 0);
        }}
        title="Select track"
      >
        {hasItems ? (
          items.map((it, i) => (
            <option key={it.id} value={it.id}>
              {it.title || `Text ${i + 1}`}
            </option>
          ))
        ) : (
          <option value="">No files yet...</option>
        )}
      </select>
      {onOpenOrder && (
        <button
          type="button"
          className="ml-2 text-xs px-3 py-1 rounded-md bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-50"
          onClick={(e) => { e.stopPropagation(); if (currentSpreadId) onOpenOrder(currentSpreadId); }}
          disabled={!currentSpreadId}
          title={orderBtnTitle}
        >
          {orderBtnText}
        </button>
      )}
      {onOpenGenerateAudio && (
        <button
          type="button"
          className="ml-2 text-xs px-3 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          onClick={async (e) => { e.stopPropagation(); await onOpenGenerateAudio(); }}
          title={generateBtnTitle}
        >
          {generateBtnText}
        </button>
      )}
      <button
        type="button"
        className="ml-2 text-gray-700 hover:text-gray-900 disabled:opacity-40"
        onClick={(e) => { e.stopPropagation(); handleDownloadAll(); }}
        disabled={!hasItems || isDownloading}
        title="Download all audio files as ZIP"
      >
        <ArrowDownTrayIcon className="w-5 h-5" />
      </button>
    </div>
  );
}
